import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { RefreshCw, Search, Package, TrendingUp, CheckCircle, AlertTriangle, Factory } from 'lucide-react';
import ExportButton from '../components/ExportButton';
import PDFExportButton from '../components/PDFExportButton';

const StockReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productFilter, setProductFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (productFilter) params.append('product', productFilter);
      if (locationFilter) params.append('location', locationFilter);
      if (stageFilter !== 'all') params.append('stage', stageFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const r = await api.get(`/sales/stock-report?${params.toString()}`);
      setData(r.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const getStageInfo = (stage) => {
    const map = {
      inventory: { label: 'Inventory', cls: 'badge-info', icon: Package },
      mrn: { label: 'MRN Received', cls: 'badge-warning', icon: TrendingUp },
      qc_passed: { label: 'QC Passed', cls: 'badge-success', icon: CheckCircle },
      finished_goods: { label: 'Finished Goods', cls: 'badge-success', icon: Factory },
    };
    return map[stage] || { label: stage, cls: 'badge-info', icon: Package };
  };

  const catLabels = { rm: 'Raw Material', wip: 'WIP', fg: 'Finished Good' };

  return (
    <div data-testid="stock-report-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Stock Report</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>Real-time stock visibility with item stage tracking</p>
        </div>
        <div className="flex gap-2">
          <ExportButton endpoint="/export/stock-report" filename="stock_report_export.xlsx" />
          <PDFExportButton endpoint="/export/pdf/stock-report" filename="stock_report_export.pdf" />
          <button onClick={fetchData} className="btn btn-secondary btn-sm" data-testid="refresh-btn"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-4 mb-4">
          <div className="stat-card" style={{ borderLeft: '4px solid #0047AB' }} data-testid="stat-total-items">
            <div className="stat-value">{data.summary.total_items}</div>
            <div className="stat-label">Total Items</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #059669' }} data-testid="stat-stock-value">
            <div className="stat-value">{data.summary.total_stock_value.toLocaleString()}</div>
            <div className="stat-label">Total Stock Value</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #D97706' }} data-testid="stat-mrn-qty">
            <div className="stat-value">{data.summary.total_mrn_qty}</div>
            <div className="stat-label">Total MRN Qty</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #6366F1' }} data-testid="stat-production">
            <div className="stat-value">{data.summary.total_production_output}</div>
            <div className="stat-label">Production Output</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4">
        <div className="flex gap-4" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ position: 'relative', flex: 2, minWidth: '200px' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
            <input type="text" className="form-input" placeholder="Search product SKU or name..." value={productFilter} onChange={(e) => setProductFilter(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="product-filter" />
          </div>
          <div style={{ minWidth: '140px' }}>
            <input type="text" className="form-input" placeholder="Location" value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} data-testid="location-filter" />
          </div>
          <select className="form-select" style={{ width: '160px' }} value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="stage-filter">
            <option value="all">All Stages</option>
            <option value="inventory">Inventory</option>
            <option value="mrn">MRN Received</option>
            <option value="qc_passed">QC Passed</option>
            <option value="finished_goods">Finished Goods</option>
          </select>
          <div style={{ minWidth: '130px' }}>
            <input type="date" className="form-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from" />
          </div>
          <div style={{ minWidth: '130px' }}>
            <input type="date" className="form-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to" />
          </div>
          <button onClick={fetchData} className="btn btn-primary btn-sm" data-testid="apply-filter-btn">Apply</button>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? <div style={{ padding: '20px' }}>Loading...</div> : !data ? <div style={{ padding: '20px', color: 'var(--text-tertiary)' }}>No data</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>SKU</th><th>Name</th><th>Category</th><th>Group</th><th>Location</th>
                  <th className="text-right">Current Stock</th><th>Unit</th>
                  <th className="text-right">MRN Qty</th><th className="text-right">QC Passed</th>
                  <th className="text-right">QC Rejected</th><th className="text-right">Prod. Output</th>
                  <th className="text-right">Value</th><th>Stage</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr><td colSpan={13} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No items match filters</td></tr>
                ) : data.items.map((item, idx) => {
                  const stageInfo = getStageInfo(item.stage);
                  const StageIcon = stageInfo.icon;
                  return (
                    <tr key={idx} data-testid={`stock-row-${item.sku}`}>
                      <td className="font-mono">{item.sku}</td>
                      <td>{item.name}</td>
                      <td><span className={`badge ${item.product_category === 'fg' ? 'badge-success' : item.product_category === 'rm' ? 'badge-info' : 'badge-warning'}`}>{catLabels[item.product_category] || item.product_category}</span></td>
                      <td>{item.item_group || '-'}</td>
                      <td>{item.location || '-'}</td>
                      <td className="text-right tabular-nums" style={{ fontWeight: 600 }}>{item.current_stock}</td>
                      <td>{item.unit}</td>
                      <td className="text-right tabular-nums" style={{ color: item.mrn_qty > 0 ? '#D97706' : 'inherit' }}>{item.mrn_qty}</td>
                      <td className="text-right tabular-nums" style={{ color: '#059669' }}>{item.qc_passed}</td>
                      <td className="text-right tabular-nums" style={{ color: item.qc_rejected > 0 ? '#DC2626' : 'inherit' }}>{item.qc_rejected}</td>
                      <td className="text-right tabular-nums" style={{ color: '#6366F1' }}>{item.production_output}</td>
                      <td className="text-right tabular-nums">{(item.current_stock * item.unit_price).toLocaleString()}</td>
                      <td>
                        <span className={`badge ${stageInfo.cls}`}>
                          <StageIcon size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                          {stageInfo.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockReport;
